import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FiAlertCircle, FiArrowLeft, FiBookOpen, FiCheckCircle, FiClock,
  FiFlag, FiHeadphones, FiPlay, FiRefreshCw, FiTarget, FiTrendingUp,
} from 'react-icons/fi';

import { useI18n } from '../i18n/I18nProvider';


const authHeaders = (json = false) => ({
  ...(json ? { 'Content-Type': 'application/json' } : {}),
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const readJsonResponse = async (response, fallbackMessage) => {
  let data;
  try {
    const raw = await response.text();
    data = raw ? JSON.parse(raw) : {};
  } catch (_) {
    throw new Error(fallbackMessage);
  }
  if (!response.ok) {
    throw new Error(data.message || fallbackMessage);
  }
  return data;
};

const Card = ({ children, className = '', ...props }) => (
  <section {...props} className={`border border-gray-200 bg-white rounded-lg shadow-sm ${className}`}>{children}</section>
);

const formatTime = (seconds) => {
  const value = Math.max(0, Math.floor(seconds));
  return `${String(Math.floor(value / 60)).padStart(2, '0')}:${String(value % 60).padStart(2, '0')}`;
};

const answerStorageKey = (sessionId) => `lexilab.adaptive-reading.${sessionId}.answers`;
const flagStorageKey = (sessionId) => `lexilab.adaptive-reading.${sessionId}.flags`;
const readingModeLabel = (mode) => (mode === 'custom' ? 'Adaptive practice' : 'IELTS simulation');
const readingScopeLabel = (scope) => ({
  recent: 'Recent words',
  today: "Today's words",
  last_session: 'Last study session',
}[scope] || 'Recent words');

const loadSavedAnswers = (sessionId) => {
  try {
    return JSON.parse(localStorage.getItem(answerStorageKey(sessionId)) || '{}');
  } catch (_) {
    return {};
  }
};

const loadSavedFlags = (sessionId) => {
  try {
    return new Set(JSON.parse(localStorage.getItem(flagStorageKey(sessionId)) || '[]'));
  } catch (_) {
    return new Set();
  }
};

function ExperimentHome({
  profile,
  sessions,
  loading,
  error,
  scope,
  onScopeChange,
  onGenerate,
  onResume,
}) {
  const { t } = useI18n();
  const sessionFocused = profile?.source_type === 'study_session';
  const [mode, setMode] = useState(sessionFocused ? 'custom' : 'mock');
  useEffect(() => {
    if (sessionFocused) setMode('custom');
  }, [sessionFocused]);
  const enough = (profile?.usable_vocabulary_count || 0) >= (profile?.minimum_vocabulary_count || 8);
  return (
    <div className="space-y-5">
      <Card className="p-5 sm:p-7">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          <div className="max-w-2xl">
            <div className="text-xs font-semibold uppercase tracking-wide text-teal-700">{t('Experimental practice')}</div>
            <h2 className="mt-1 text-2xl sm:text-3xl font-bold text-gray-900">{t('Adaptive IELTS-style Reading')}</h2>
            <p className="mt-3 text-gray-600 leading-7">
              {t(sessionFocused
                ? 'This reading is built from the vocabulary, exercises, errors, and hints in the study session you just completed.'
                : 'A fresh 20-minute passage built around your recent vocabulary, review history, and sentence-ordering difficulties.')}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="mt-7 flex items-center gap-3 text-gray-600"><FiRefreshCw className="animate-spin" /> {t('Reading your recent learning history...')}</div>
        ) : profile ? (
          <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 border-y border-gray-200">
            <div className="py-4 sm:pr-3"><div className="text-xs uppercase font-semibold text-gray-500">{t('Vocabulary pool')}</div><div className="text-2xl font-bold mt-1">{profile.usable_vocabulary_count}</div></div>
            <div className="py-4 sm:px-3 border-t sm:border-t-0 sm:border-l"><div className="text-xs uppercase font-semibold text-gray-500">{t('History window')}</div><div className="text-2xl font-bold mt-1">{profile.lookback_days} {t('days')}</div></div>
            <div className="py-4 sm:pl-3 border-t sm:border-t-0 sm:border-l"><div className="text-xs uppercase font-semibold text-gray-500">{t('Recent accuracy')}</div><div className="text-2xl font-bold mt-1">{profile.recent_accuracy == null ? '—' : `${profile.recent_accuracy}%`}</div></div>
          </div>
        ) : null}

        <div className="mt-6">
          <div className="font-semibold text-gray-900">{t('Choose vocabulary scope')}</div>
          <div className="mt-3 grid sm:grid-cols-3 border rounded-md overflow-hidden" role="group">
            {[
              {
                value: 'recent',
                label: 'Recent words',
                description: 'Uses your recent learning and review history.',
              },
              {
                value: 'today',
                label: "Today's words",
                description: 'Uses words learned or reviewed today.',
              },
              {
                value: 'last_session',
                label: 'Last study session',
                description: 'Uses the exact words and exercises from your most recent completed session.',
              },
            ].map((choice) => (
              <button
                key={choice.value}
                type="button"
                onClick={() => onScopeChange(choice.value)}
                aria-pressed={scope === choice.value}
                className={`px-4 py-4 text-left border-b last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0 ${scope === choice.value ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                <span className="block font-semibold">{t(choice.label)}</span>
                <span className={`block mt-1 text-sm leading-5 ${scope === choice.value ? 'text-indigo-50' : 'text-gray-500'}`}>{t(choice.description)}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6">
          <div className="font-semibold text-gray-900">{t('Choose a practice mode')}</div>
          <div className="mt-3 grid sm:grid-cols-2 border rounded-md overflow-hidden" role="group">
            {[
              { value: 'mock', label: 'IELTS simulation', description: 'Closely follows IELTS Academic reading structure, pacing, and question standards.' },
              { value: 'custom', label: 'Adaptive practice', description: 'Targets your vocabulary, sentence-ordering errors, and current learning level.' },
            ].map((choice) => (
              <button
                key={choice.value}
                onClick={() => setMode(choice.value)}
                aria-pressed={mode === choice.value}
                className={`px-4 py-4 text-left border-b last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0 ${mode === choice.value ? 'bg-teal-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                <span className="block font-semibold">{t(choice.label)}</span>
                <span className={`block mt-1 text-sm leading-5 ${mode === choice.value ? 'text-teal-50' : 'text-gray-500'}`}>{t(choice.description)}</span>
              </button>
            ))}
          </div>
        </div>

        {error && <div className="mt-5 flex gap-2 border border-rose-200 bg-rose-50 text-rose-800 rounded-md px-4 py-3"><FiAlertCircle className="mt-0.5 shrink-0" /> {t(error)}</div>}
        {!loading && !enough && <p className="mt-4 text-sm text-amber-800">{t('Complete a little more vocabulary practice before generating this reading.')}</p>}
        <div className="mt-6 flex justify-end">
          <button disabled={!enough} onClick={() => onGenerate(mode)} className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold px-5 py-3 rounded-md disabled:opacity-50">
            <FiPlay /> {t(mode === 'custom' ? 'Generate adaptive practice' : 'Generate IELTS simulation')}
          </button>
        </div>
      </Card>

      {sessions.length > 0 && (
        <Card className="p-5 sm:p-6">
          <h3 className="font-bold text-lg text-gray-900">{t('Recent adaptive readings')}</h3>
          <div className="mt-3 divide-y">
            {sessions.map((session) => (
              <button key={session.id} onClick={() => onResume(session.id)} className="w-full py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-left hover:bg-gray-50 px-2">
                <div>
                  <div data-i18n-skip className="font-semibold text-gray-900">{session.title}</div>
                  <div className="text-sm text-gray-500 mt-1">
                    {new Date(session.created_at).toLocaleString()} · {t(readingModeLabel(session.mode))} · {t(readingScopeLabel(session.scope))}
                  </div>
                </div>
                <div className="text-sm font-medium text-teal-700">
                  {session.status === 'completed' ? `${session.correct}/${session.total}` : t('Continue reading')}
                </div>
              </button>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function GeneratingReading() {
  const { t } = useI18n();
  const [stage, setStage] = useState(0);
  const stages = [
    'Selecting useful recent vocabulary',
    'Writing a coherent academic passage',
    'Building IELTS-style questions',
    'Checking every answer against the passage',
  ];
  useEffect(() => {
    const timer = window.setInterval(() => setStage((value) => Math.min(stages.length - 1, value + 1)), 11000);
    return () => window.clearInterval(timer);
  }, [stages.length]);
  return (
    <Card className="p-8 sm:p-12 text-center">
      <FiRefreshCw className="animate-spin mx-auto text-4xl text-teal-600" />
      <h2 className="mt-5 text-2xl font-bold">{t('Preparing your adaptive reading')}</h2>
      <p className="mt-2 text-gray-600">{t(stages[stage])}</p>
      <div className="mt-7 max-w-xl mx-auto h-2 bg-gray-100 rounded overflow-hidden">
        <div className="h-full bg-teal-500 animate-pulse" style={{ width: `${25 + stage * 20}%` }} />
      </div>
      <p className="mt-4 text-sm text-gray-500">{t('The passage is saved before you begin, so it can be resumed later.')}</p>
    </Card>
  );
}

function QuestionInput({ question, value, onChange, disabled }) {
  const { t } = useI18n();
  if (question.type === 'sentence_completion') {
    return (
      <input
        value={value || ''}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        placeholder={t('Type your answer')}
        className="mt-3 w-full border rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 disabled:bg-gray-100"
      />
    );
  }
  const options = question.type === 'true_false_not_given'
    ? [
      { key: 'TRUE', text: 'True' },
      { key: 'FALSE', text: 'False' },
      { key: 'NOT_GIVEN', text: 'Not given' },
    ]
    : question.options;
  return (
    <div className="mt-3 space-y-2">
      {options.map((option) => (
        <label key={option.key} className={`flex gap-3 border rounded-md px-3 py-2 cursor-pointer ${value === option.key ? 'border-teal-500 bg-teal-50' : 'hover:bg-gray-50'}`}>
          <input type="radio" disabled={disabled} checked={value === option.key} onChange={() => onChange(option.key)} />
          <span><strong className="mr-2">{option.key}</strong>{t(option.text)}</span>
        </label>
      ))}
    </div>
  );
}

function ReadingRunner({ session, onSubmit }) {
  const { t, locale } = useI18n();
  const reading = session.reading;
  const [answers, setAnswers] = useState(() => loadSavedAnswers(session.id));
  const [flags, setFlags] = useState(() => loadSavedFlags(session.id));
  const [submitting, setSubmitting] = useState(false);
  const startedAt = useRef(new Date(session.started_at || session.created_at).getTime() || Date.now());
  const deadline = startedAt.current + 20 * 60 * 1000;
  const [remaining, setRemaining] = useState(() => Math.max(0, Math.ceil((deadline - Date.now()) / 1000)));
  const submitRef = useRef(null);

  const submit = useCallback(async (forced = false) => {
    const unanswered = reading.questions.filter((question) => !String(answers[question.id] || '').trim()).length;
    if (!forced && unanswered > 0 && !window.confirm(t('Some questions are unanswered. Submit anyway?'))) return;
    setSubmitting(true);
    try {
      await onSubmit({
        answers,
        duration_seconds: Math.round((Date.now() - startedAt.current) / 1000),
        language: locale,
      });
      localStorage.removeItem(answerStorageKey(session.id));
      localStorage.removeItem(flagStorageKey(session.id));
    } finally {
      setSubmitting(false);
    }
  }, [answers, locale, onSubmit, reading.questions, session.id, t]);
  submitRef.current = submit;

  useEffect(() => {
    localStorage.setItem(answerStorageKey(session.id), JSON.stringify(answers));
  }, [answers, session.id]);
  useEffect(() => {
    localStorage.setItem(flagStorageKey(session.id), JSON.stringify([...flags]));
  }, [flags, session.id]);
  useEffect(() => {
    if (deadline <= Date.now()) {
      submitRef.current?.(true);
      return undefined;
    }
    const timer = window.setInterval(() => {
      const next = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setRemaining(next);
      if (next === 0) {
        window.clearInterval(timer);
        submitRef.current?.(true);
      }
    }, 1000);
    return () => window.clearInterval(timer);
  }, [deadline]);

  const answered = Object.values(answers).filter((value) => String(value).trim()).length;
  return (
    <div className="space-y-4">
      <div className="sticky top-0 z-20 bg-white border rounded-md px-4 py-3 flex items-center justify-between shadow-sm">
        <div className="text-sm text-gray-600"><strong className="text-gray-900">{answered}/{reading.questions.length}</strong> {t('answered')}</div>
        <div className={`inline-flex items-center gap-2 font-mono text-lg font-bold ${remaining < 180 ? 'text-rose-600' : 'text-gray-900'}`}><FiClock /> {formatTime(remaining)}</div>
      </div>
      <div className="grid lg:grid-cols-[minmax(0,1.15fr)_minmax(360px,0.85fr)] gap-5 items-start">
        <Card className="p-5 sm:p-7 lg:sticky lg:top-20 max-h-none lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto">
          <div className="text-xs font-semibold uppercase tracking-wide text-teal-700">{t(readingModeLabel(session.mode))}</div>
          <h1 data-i18n-skip className="mt-2 text-2xl sm:text-3xl font-bold text-gray-900">{reading.title}</h1>
          <div className="mt-6 space-y-5 text-[17px] leading-8 text-gray-800">
            {reading.paragraphs.map((paragraph) => (
              <p key={paragraph.id}><strong data-i18n-skip className="mr-2 text-gray-500">{paragraph.id}</strong><span data-i18n-skip>{paragraph.text}</span></p>
            ))}
          </div>
        </Card>
        <div className="space-y-4">
          <Card className="p-4">
            <div className="flex flex-wrap gap-2">
              {reading.questions.map((question, index) => (
                <a key={question.id} href={`#question-${question.id}`} className={`w-9 h-9 inline-flex items-center justify-center border rounded ${answers[question.id] ? 'bg-teal-600 text-white border-teal-600' : flags.has(question.id) ? 'bg-amber-100 border-amber-400' : 'bg-white'}`}>{index + 1}</a>
              ))}
            </div>
          </Card>
          {reading.questions.map((question, index) => (
            <Card key={question.id} className="p-5 scroll-mt-24" id={`question-${question.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-xs uppercase font-semibold tracking-wide text-gray-500">{t(question.type.replaceAll('_', ' '))}</div>
                  <h3 className="mt-2 font-semibold text-gray-900"><span className="mr-2">{index + 1}.</span><span data-i18n-skip>{question.prompt}</span></h3>
                </div>
                <button title={t('Review later')} onClick={() => setFlags((current) => {
                  const next = new Set(current);
                  if (next.has(question.id)) next.delete(question.id); else next.add(question.id);
                  return next;
                })} className={`p-2 rounded ${flags.has(question.id) ? 'bg-amber-100 text-amber-700' : 'text-gray-400 hover:bg-gray-100'}`}><FiFlag /></button>
              </div>
              <QuestionInput question={question} value={answers[question.id]} onChange={(value) => setAnswers((current) => ({ ...current, [question.id]: value }))} disabled={submitting} />
            </Card>
          ))}
          <button disabled={submitting} onClick={() => submit(false)} className="w-full inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md px-5 py-3 font-semibold disabled:opacity-50">
            {submitting ? <FiRefreshCw className="animate-spin" /> : <FiCheckCircle />} {submitting ? t('Reviewing your result...') : t('Submit reading')}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReadingResult({ session, onNew, onRetry, retrying, onContinue }) {
  const { t } = useI18n();
  const { score, analysis, reading } = session;
  const byType = useMemo(() => {
    const groups = {};
    (score?.results || []).forEach((result) => {
      const group = groups[result.type] || { correct: 0, total: 0 };
      group.total += 1;
      if (result.correct) group.correct += 1;
      groups[result.type] = group;
    });
    return groups;
  }, [score]);
  return (
    <div className="space-y-5">
      <Card className="p-6 sm:p-8">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-5">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-teal-700">{t('Adaptive reading result')}</div>
            <h2 data-i18n-skip className="mt-1 text-2xl sm:text-3xl font-bold">{reading.title}</h2>
            <p className="mt-2 text-gray-600">{t(readingModeLabel(session.mode))} · {formatTime(session.duration_seconds)}</p>
          </div>
          <div className="bg-teal-50 border border-teal-200 px-6 py-4 min-w-[180px]">
            <div className="text-sm text-teal-800">{t('Reading score')}</div>
            <div className="text-4xl font-bold text-teal-900 mt-1">{score.correct}/{score.total}</div>
            <div className="text-sm text-teal-800 mt-1">{score.accuracy}%</div>
          </div>
        </div>
        <div className="mt-6 grid sm:grid-cols-3 border-y">
          {Object.entries(byType).map(([type, metric]) => (
            <div key={type} className="py-4 px-3 first:pl-0 border-l first:border-l-0">
              <div className="text-xs uppercase font-semibold text-gray-500">{t(type.replaceAll('_', ' '))}</div>
              <div className="font-bold text-xl mt-1">{metric.correct}/{metric.total}</div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-6 sm:p-7">
        <div className="flex items-center gap-2 text-teal-700"><FiTrendingUp /><span className="text-xs uppercase font-semibold tracking-wide">{t('AI coaching review')}</span></div>
        <p className="mt-3 text-lg leading-8 text-gray-800">{analysis?.summary}</p>
        <div className="mt-5 grid md:grid-cols-2 gap-6">
          <div><h3 className="font-bold flex items-center gap-2"><FiCheckCircle className="text-emerald-600" /> {t('What went well')}</h3><ul className="mt-2 space-y-2 text-gray-700 list-disc pl-5">{(analysis?.strengths || []).map((item, index) => <li key={index}>{item}</li>)}</ul></div>
          <div><h3 className="font-bold flex items-center gap-2"><FiTarget className="text-indigo-600" /> {t('Next practice focus')}</h3><ul className="mt-2 space-y-2 text-gray-700 list-disc pl-5">{(analysis?.next_steps || []).map((item, index) => <li key={index}>{item}</li>)}</ul></div>
        </div>
        <div className="mt-5 border-l-4 border-indigo-400 bg-indigo-50 px-4 py-3 text-indigo-900"><strong>{t('Learning observation')}: </strong>{analysis?.estimated_performance}</div>
      </Card>

      <Card className="p-5 sm:p-6">
        <h3 className="font-bold text-lg">{t('Review every answer')}</h3>
        <div className="mt-3 divide-y">
          {score.results.map((result, index) => (
            <details key={result.id} className="py-4">
              <summary className="cursor-pointer flex items-start gap-3">
                <span className={`mt-0.5 ${result.correct ? 'text-emerald-600' : 'text-rose-600'}`}>{result.correct ? <FiCheckCircle /> : <FiAlertCircle />}</span>
                <span><strong className="mr-2">{index + 1}.</strong><span data-i18n-skip>{result.prompt}</span></span>
              </summary>
              <div className="mt-3 ml-8 text-sm space-y-2 text-gray-700">
                <p><strong>{t('Your answer')}:</strong> <span data-i18n-skip>{result.response || '—'}</span></p>
                <p><strong>{t('Correct answer')}:</strong> <span data-i18n-skip>{result.answer}</span></p>
                <p><strong>{t('Evidence')}:</strong> <span data-i18n-skip>{result.evidence}</span></p>
                <p><strong>{t('Why')}:</strong> <span data-i18n-skip>{result.explanation}</span></p>
              </div>
            </details>
          ))}
        </div>
      </Card>
      <div className="flex flex-wrap justify-end gap-3">
        <button onClick={onNew} className="inline-flex items-center gap-2 border rounded-md px-4 py-2 hover:bg-gray-50"><FiRefreshCw /> {t('Create another reading')}</button>
        <button
          disabled={retrying}
          onClick={onRetry}
          className="inline-flex items-center gap-2 border border-indigo-300 text-indigo-700 rounded-md px-4 py-2 hover:bg-indigo-50 disabled:opacity-50"
        >
          <FiBookOpen /> {retrying ? t('Opening a new attempt...') : t('Practice this article again')}
        </button>
        {onContinue && (
          <button onClick={onContinue} className="inline-flex items-center gap-2 bg-indigo-600 text-white rounded-md px-4 py-2 hover:bg-indigo-700">
            <FiHeadphones /> {t('Continue to dictation')}
          </button>
        )}
      </div>
    </div>
  );
}

export default function AdaptiveReadingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useI18n();
  const sourceSessionId = searchParams.get('source_session_id') || '';
  const requestedReadingSessionId = searchParams.get('session_id') || '';
  const continueToFollowup = searchParams.get('after') === 'followup';
  const requestedScope = searchParams.get('scope') || '';
  const assignmentId = searchParams.get('assignment_id') || '';
  const assignmentTargetId = searchParams.get('target_id') || '';
  const assignmentAttemptId = searchParams.get('attempt_id') || '';
  const assignedMode = searchParams.get('mode') || 'custom';
  const assignedAutoStartRef = useRef(false);
  const [scope, setScope] = useState(
    sourceSessionId
      ? 'last_session'
      : (['recent', 'today', 'last_session'].includes(requestedScope) ? requestedScope : 'recent'),
  );
  const [phase, setPhase] = useState('home');
  const [profile, setProfile] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retrying, setRetrying] = useState(false);
  const openedRequestedSession = useRef('');

  const loadHome = useCallback(async () => {
    setLoading(true);
    try {
      const profileParams = new URLSearchParams({ scope });
      if (scope === 'last_session' && sourceSessionId) {
        profileParams.set('source_session_id', sourceSessionId);
      }
      const [profileResponse, sessionsResponse] = await Promise.all([
        fetch(`/api/student/self-check/adaptive-reading/profile?${profileParams.toString()}`, { headers: authHeaders() }),
        fetch('/api/student/self-check/adaptive-reading/sessions?limit=8', { headers: authHeaders() }),
      ]);
      const [profileData, sessionsData] = await Promise.all([
        readJsonResponse(profileResponse, 'Could not read your learning history'),
        readJsonResponse(sessionsResponse, 'Could not read your learning history'),
      ]);
      setProfile(profileData.profile);
      setSessions(sessionsData.sessions || []);
      setError('');
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  }, [scope, sourceSessionId]);
  useEffect(() => { loadHome(); }, [loadHome]);

  const openSession = async (sessionId) => {
    setError('');
    try {
      const response = await fetch(`/api/student/self-check/adaptive-reading/sessions/${sessionId}`, { headers: authHeaders() });
      const data = await readJsonResponse(response, 'Could not open this reading');
      if (data.session.status === 'completed' && data.session.result_id) {
        navigate(`/result/${encodeURIComponent(data.session.result_id)}`);
        return;
      }
      setSession(data.session);
      setPhase(data.session.status === 'completed' ? 'result' : 'test');
    } catch (openError) {
      setError(openError.message);
    }
  };

  useEffect(() => {
    if (
      requestedReadingSessionId
      && openedRequestedSession.current !== requestedReadingSessionId
    ) {
      openedRequestedSession.current = requestedReadingSessionId;
      openSession(requestedReadingSessionId);
    }
  }, [requestedReadingSessionId]);

  const retrySession = async () => {
    if (!session?.id || retrying) return;
    setRetrying(true);
    setError('');
    try {
      const response = await fetch(
        `/api/student/self-check/adaptive-reading/sessions/${session.id}/retry`,
        {
          method: 'POST',
          headers: authHeaders(),
        },
      );
      const data = await readJsonResponse(response, 'Could not reopen this reading');
      setSession(data.session);
      setPhase('test');
    } catch (retryError) {
      setError(retryError.message);
    } finally {
      setRetrying(false);
    }
  };

  const generate = async (mode) => {
    setPhase('generating');
    setError('');
    try {
      const response = await fetch('/api/student/self-check/adaptive-reading/generate', {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({
          mode,
          scope,
          source_session_id: scope === 'last_session' ? (sourceSessionId || undefined) : undefined,
          assignment_id: assignmentId || undefined,
          target_id: assignmentTargetId || undefined,
          attempt_id: assignmentAttemptId || undefined,
        }),
      });
      const data = await readJsonResponse(
        response,
        'This reading could not be prepared yet. Please try again.',
      );
      setSession(data.session);
      setPhase('test');
    } catch (generateError) {
      setError(generateError.message);
      setPhase('home');
    }
  };

  useEffect(() => {
    if (!assignmentId || !assignmentTargetId || !assignmentAttemptId || !profile || phase !== 'home' || assignedAutoStartRef.current) return;
    assignedAutoStartRef.current = true;
    generate(['mock', 'custom'].includes(assignedMode) ? assignedMode : 'custom');
  }, [assignmentAttemptId, assignmentId, assignedMode, assignmentTargetId, phase, profile]);

  const submit = async (payload) => {
    try {
      const response = await fetch(`/api/student/self-check/adaptive-reading/sessions/${session.id}/submit`, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify(payload),
      });
      const data = await readJsonResponse(response, 'Your reading could not be submitted');
      if (data.session?.result_id) {
        navigate(`/result/${encodeURIComponent(data.session.result_id)}`, { replace: true });
        return;
      }
      setSession(data.session);
      setPhase('result');
    } catch (submitError) {
      setError(submitError.message);
      throw submitError;
    }
  };

  return (
    <div className="max-w-[1500px] mx-auto pb-16">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <button onClick={() => navigate('/student/self-check')} className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"><FiArrowLeft /> {t('Back to Self Check')}</button>
          <div className="mt-4 text-xs uppercase font-semibold tracking-wide text-teal-700">{t('More experiments')}</div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-1">{t('Adaptive IELTS-style Reading')}</h1>
        </div>
        <FiBookOpen className="hidden sm:block text-4xl text-teal-600" />
      </header>
      {error && phase !== 'home' && <div className="mb-5 border border-rose-200 bg-rose-50 text-rose-800 rounded-md px-4 py-3">{t(error)}</div>}
      {phase === 'home' && (
        <ExperimentHome
          profile={profile}
          sessions={sessions}
          loading={loading}
          error={error}
          scope={scope}
          onScopeChange={setScope}
          onGenerate={generate}
          onResume={openSession}
        />
      )}
      {phase === 'generating' && <GeneratingReading />}
      {phase === 'test' && session && <ReadingRunner session={session} onSubmit={submit} />}
      {phase === 'result' && session && (
        <ReadingResult
          session={session}
          onNew={() => { setPhase('home'); loadHome(); }}
          onRetry={retrySession}
          retrying={retrying}
          onContinue={
            continueToFollowup && session.source_session_id === sourceSessionId
              ? () => navigate('/student/self-check?post_learning_followup=1')
              : null
          }
        />
      )}
    </div>
  );
}
